import { flash, Label, Modal, SubmitButton } from "@xola/ui-kit";
import { FormField, InputField, nullableString, requiredEmail, requiredString } from "common";
import { isEmpty } from "lodash";
import React from "react";
import { useMutation } from "react-query";
import { Traveler } from "types";
import { object } from "yup";
import { NoTranslate } from "../../Common/NoTranslate";
import { RadioInput } from "../../Common/RadioInput";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { EditedFormValues } from "./EditPrimaryContactModal";
import { api } from "@/api/api";
import { populateResource } from "@/hooks/helper";
import { useSeller } from "@/hooks/useAccount";
import { useCollection } from "@/hooks/useCollection";
import { useInvalidate } from "@/hooks/useInvalidation";

const getValidationSchema = (contact: Traveler, existingContact?: Traveler) => {
    return object().shape({
        name: requiredString(),
        phone: nullableString().when([], {
            // force user to select one of the existing phones if present, since merge will anyway keep
            // the phone number even if we pass empty from the UI
            is: () => !isEmpty(contact.phone) || !isEmpty(existingContact?.phone),
            then: () => requiredString(),
        }),
        email: requiredEmail(),
    });
};

interface MergeContactsModalProps {
    contact: Traveler;
    editedValues?: EditedFormValues;
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (mergedContactId: string) => void;
}

export const MergeContactsModal = ({ contact, editedValues, isOpen, onClose, onSuccess }: MergeContactsModalProps) => {
    const { data: existingData } = useCollection<Traveler>(
        "users",
        { email: editedValues?.email },
        { enabled: !!editedValues?.email },
    );

    return (
        <Modal isOpen={isOpen} size="huge">
            <ModalContent
                contact={contact}
                editedValues={editedValues}
                existingContact={existingData?.[0]}
                onClose={onClose}
                onSuccess={onSuccess}
            />
        </Modal>
    );
};

interface FormValues {
    name: string;
    phone: string;
    email?: string;
}

export interface MergeContactPayload {
    _emailOverrides: { sendEmailToTraveler: boolean };
    mergedTo: {
        id?: ID;
        name: string;
        email?: string;
        phone: string;
    };
    seller: ID;
    additionalNames: string[];
    additionalPhones: string[];
}

const initialValues: FormValues = {
    name: "",
    phone: "",
    email: "",
};

interface ModalContentProps {
    contact: Traveler;
    editedValues?: EditedFormValues;
    existingContact?: Traveler;
    onClose: () => void;
    onSuccess: (mergedContactId: string) => void;
}

const ModalContent = ({ contact, editedValues, existingContact, onClose, onSuccess }: ModalContentProps) => {
    const seller = useSeller();
    const invalidate = useInvalidate();

    const { mutateAsync: mergeContact, isLoading } = useMutation(async (values: FormValues) => {
        const additionalNames =
            existingContact?.name && values.name !== existingContact.name ? [existingContact.name] : [];
        const additionalPhones =
            existingContact?.phone && values.phone !== existingContact.phone ? [existingContact.phone] : [];
        const payload: MergeContactPayload = {
            _emailOverrides: { sendEmailToTraveler: true },
            mergedTo: {
                id: existingContact?.id,
                name: values.name,
                email: values.email,
                phone: values.phone,
            },
            seller: seller.id,
            additionalNames,
            additionalPhones,
        };
        return api.patch(`users/${contact.id}`, payload);
    });

    const isPhoneRequired = !isEmpty(contact.phone) || !isEmpty(existingContact?.phone);

    const handleSubmit = async (values: FormValues) => {
        if (existingContact?.id) {
            await mergeContact(values);

            // Refresh the pre-merge cached survivor so onSuccess doesn't read stale name/phone.
            populateResource("users", { ...existingContact, ...values });
            invalidate("users", existingContact.id);
            invalidate("users", contact.id);

            flash.success("Contact details merged");
            onSuccess(existingContact?.id);
        }
    };

    return (
        <XolaFormik
            validateOnBlur={false}
            validationSchema={getValidationSchema(contact, existingContact)}
            initialValues={getFormValues(editedValues!)}
            onReset={onClose}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, setFieldValue, values }) => (
                <>
                    <Modal.Header
                        description={
                            <>
                                <var data-var="email">{editedValues?.email}</var> is already associated with an existing
                                customer. Would you like to merge customer records?
                            </>
                        }
                    >
                        Email Already in Use
                    </Modal.Header>

                    <Modal.Body className="flex-col">
                        <div className="mb-6 rounded-md bg-yellow-lighter py-2 text-center">
                            All saved payment info, booking history, notes and waivers associated with both records will
                            be merged.
                        </div>

                        <div className="mb-2 grid grid-cols-2">
                            <h3>Edited Customer</h3>
                            <h3>Duplicate Customer</h3>
                        </div>

                        <Label>Name</Label>
                        <div className="grid grid-cols-2" data-testid="merge-contacts-modal-name-row">
                            <RadioInput
                                checked={contact.name === values.name}
                                onChange={() => setFieldValue("name", contact.name)}
                            >
                                <NoTranslate>{contact.name}</NoTranslate>
                            </RadioInput>
                            <RadioInput
                                checked={existingContact?.name === values.name}
                                onChange={() => setFieldValue("name", existingContact?.name)}
                            >
                                <NoTranslate>{existingContact?.name}</NoTranslate>
                            </RadioInput>
                        </div>
                        <hr className="my-2 text-gray-light" />

                        <Label>Email</Label>
                        <div className="grid grid-cols-2" data-testid="merge-contacts-modal-email-row">
                            <NoTranslate>{contact.email}</NoTranslate>
                            <NoTranslate>{existingContact?.email}</NoTranslate>
                        </div>
                        <hr className="my-2 text-gray-light" />

                        <Label>Phone</Label>
                        <div className="grid grid-cols-2" data-testid="merge-contacts-modal-phone-row">
                            <RadioInput
                                checked={!isEmpty(contact.phone) && contact.phone === values.phone}
                                disabled={isEmpty(contact.phone)}
                                onChange={() => setFieldValue("phone", contact.phone)}
                            >
                                <NoTranslate>{contact.phone}</NoTranslate>
                            </RadioInput>
                            <RadioInput
                                checked={!isEmpty(existingContact?.phone) && existingContact?.phone === values.phone}
                                disabled={isEmpty(existingContact?.phone)}
                                onChange={() => setFieldValue("phone", existingContact?.phone)}
                            >
                                <NoTranslate>{existingContact?.phone}</NoTranslate>
                            </RadioInput>
                        </div>
                        <hr className="my-2 text-gray-light" />

                        <div className="mt-6 rounded-md border border-gray-light bg-gray-lighter p-6">
                            <h3 className="mb-2">Merged Customer Record</h3>
                            <fieldset className="space-y-4" disabled={isLoading}>
                                <FormField name="name" label="Name">
                                    <InputField isRequired />
                                </FormField>
                                <FormField className="opacity-50" name="email" label="Email">
                                    <InputField readOnly />
                                </FormField>
                                <FormField name="phone" label="Phone">
                                    <InputField isRequired={isPhoneRequired} />
                                </FormField>
                            </fieldset>
                        </div>
                    </Modal.Body>

                    <Modal.Footer className="flex justify-end space-x-4">
                        <SecondaryButton disabled={isLoading} onClick={onClose}>
                            Cancel
                        </SecondaryButton>

                        <SubmitButton type="submit" isLoading={isLoading} onClick={handleSubmit}>
                            Update and Merge
                        </SubmitButton>
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};

const getFormValues = (contact: EditedFormValues): FormValues => {
    return {
        ...initialValues,
        name: contact?.name,
        phone: contact?.phone,
        email: contact?.email,
    };
};
